import moment from "moment";
import { BehaviorSubject } from "rxjs";
import { IDataResponse, apiNoLoading, STATUS_CODE } from "src/framework/providers/api";
import { LIST_TIPO_CONTA_DISTRIBUIDORES, TAtributos } from "src/types";
import { LocalStorage } from "src/framework/commons/LocalStorage";
import { ContaUsuario } from "./global.store.types";

export type CacheType<T = any> = {
    expires: number;
    data: T;
    loading: boolean;
}

export type TUploadConifg = {
    url: string;
    port: string | number;
};

export type segmento = "on" | "off" | null;
export const SEGMENTO_DEFAULT = "on"

export type venda = "gerador" | "avulso";
export const VENDA_DEFAULT = "gerador";

export const MINIO_URL = process.env.REACT_APP_MINIO_UPLOAD_URL || '';

export const padronizaSegmento = (segmento: segmento | null): segmento => {
    if (!segmento) return SEGMENTO_DEFAULT
    if (!['on', 'off'].includes(segmento)) return SEGMENTO_DEFAULT
    return segmento
}

export const padronizaVenda = (venda: venda | null): venda => {
    if (!venda) return VENDA_DEFAULT;
    if (!['gerador', 'avulso'].includes(venda)) return VENDA_DEFAULT;
    return venda;
}

const getChacheTime = (customCacheTime?: number): number => {
    return Number(moment().add(customCacheTime || process.env.REACT_APP_ENV_CACHE_EXPIRES, "minute").format("x"))
}

export const contaUsuario$ = new BehaviorSubject<CacheType<ContaUsuario | null>>({
    data: null,
    expires: 0,
    loading: false,
});

export const noPropagateDialog$ = new BehaviorSubject<boolean>(false)

export const segmento$ = new BehaviorSubject<segmento>(SEGMENTO_DEFAULT)

export const tipoVenda$ = new BehaviorSubject<venda>(VENDA_DEFAULT)

export const estadoId$ = new BehaviorSubject<string>("")

export const selos$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const atributos$ = new BehaviorSubject<CacheType<TAtributos[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const estruturas$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const estados$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const cidades$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});


export const grupoEstrutura$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const componentes$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const distribuidores$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const paises$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const bancos$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
});

export const uploadUrl$ = new BehaviorSubject<CacheType<any>>({
    data: {},
    expires: 0,
    loading: false,
})

export const avulsos$ = new BehaviorSubject<CacheType<any[]>>({
    data: [],
    expires: 0,
    loading: false,
})

export const resolveUploadUrl = (upload: CacheType<TUploadConifg> | null) => { 
    const uploadConfig = upload?.data || {
        url: "",
        port: ""
    }

    let url: string = uploadConfig?.url || "";

    if (uploadConfig?.port && uploadConfig?.port !== 80) {
      url = `${url}:${uploadConfig["port"]}`;
    }
    return url;
}

export const resolveProtocolUrl = (url?: string): string => {
    if (!url) return '';
    if (url?.includes('data:image')) return url;
    if (url.includes('//undefined')) return url.replace('//undefined', `//${MINIO_URL}`);
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    return url;
};

export const getContaUsuario = async() => {
    const now = moment().format("x")
    const { expires, loading } = contaUsuario$.getValue();

    if (expires < Number(now) && !loading) {
        contaUsuario$.next({ ...contaUsuario$.getValue(), loading: true })
        try {
            const response: IDataResponse = await apiNoLoading.get({
                url: `/cadastros/contas/usuario/conta_por_usuario`,
            });

            if (response.statusCode === STATUS_CODE.OK) {
                const data = response.data;
                const newExpireDate = getChacheTime()
                contaUsuario$.next({
                    data: data,
                    expires: newExpireDate,
                    loading: false
                })
                return data;
            }
            return contaUsuario$.getValue().data;
        } catch (error) {
            contaUsuario$.next({
                data: null,
                expires: 0,
                loading: false
            })
            return []
        }
    }

    return contaUsuario$.getValue().data;
}

export const uploadUrl = async (): Promise<CacheType<TUploadConifg> | null> => {
    const now = moment().format("x")
    const _cached = LocalStorage.get<CacheType | null>("solar.uploads", true);

    const expires = _cached?.expires || 0;
    const loading = _cached?.loading || false;

    if (expires < Number(now) && !loading) {
        LocalStorage.set("solar.upload", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/uploads`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.uploads", cache, true)

            return LocalStorage.get("solar.uploads", true) || null;
        }
        return null;
    }

    return LocalStorage.get("solar.upload", true) || null;

  }

export const getSelos = async (): Promise<any[]> => {
    const now = moment().format("x")
    const _cached = LocalStorage.get<CacheType | null>("solar.selos", true);

    const expires = _cached?.expires || 0;
    const loading = _cached?.loading || false;

    if (expires < Number(now) && !loading) {
        LocalStorage.set("solar.selos", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/selos`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.selos", cache, true)

            return cache.data;
        }
        return [];
    }

    return LocalStorage.get("solar.selos", true) || [];
}

export const getAtributos = async (): Promise<any[]> => {
    const now = moment().format("x")
    const _cached = LocalStorage.get<CacheType | null>("solar.atributos", true);

    const expires = _cached?.expires || 0;
    const loading = _cached?.loading || false;

    if (expires < Number(now) && !loading) {
        LocalStorage.set("solar.atributos", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/cadastros/atributos`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.atributos", cache, true)

            return cache.data;
        }
        return [];
    }

    return LocalStorage.get("solar.atributos", true) || [];
}

export const getEstruturas = async (): Promise<any[]> => {
    const now = moment().format("x")
    const _cached = LocalStorage.get<CacheType | null>("solar.estruturas", true);

    const expires = _cached?.expires || 0;
    const loading = _cached?.loading || false;

    if (expires < Number(now) && !loading) {
        LocalStorage.set("solar.estruturas", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/estruturas`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.estruturas", cache, true)

            return cache.data;
        }
        return [];
    }

    return LocalStorage.get("solar.estruturas", true) || [];
}


export const getEstados = async (): Promise<any[]> => {
    const now = moment().format("x")
    const { expires, loading } = estados$.getValue();
    if (expires < Number(now) && !loading) {
        estados$.next({ ...estados$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `estados?orderby=nome`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()
            estados$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        }
        return estados$.getValue().data;
    }

    return estados$.getValue().data;

}


export const getCidades = async (): Promise<any[]> => {
    const now = moment().format("x")
    const { expires, loading } = cidades$.getValue();
    if (expires < Number(now) && !loading) {
        cidades$.next({ ...cidades$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `cidades?orderby=estado_id,nome`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()
            cidades$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        }
        return cidades$.getValue().data;
    }

    return cidades$.getValue().data;

}

export const getGruposEstrutura = async (): Promise<any[]> => {
    const now = moment().format("x")
    const { expires, loading } = grupoEstrutura$.getValue();
    if (expires < Number(now) && !loading) {
        grupoEstrutura$.next({ ...grupoEstrutura$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `/estoque/grupos_estruturas?orderby=nome`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()
            grupoEstrutura$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        }
        return grupoEstrutura$.getValue().data;
    }

    return grupoEstrutura$.getValue().data;

}

export const getComponentes = async (): Promise<CacheType | null> => {
    const now = moment().format("x")
    const _cached = LocalStorage.get<CacheType | null>("solar.componentes", true);

    const expires = _cached?.expires || 0;
    const loading = _cached?.loading || false;

    if (expires < Number(now) && !loading) {
        LocalStorage.set("solar.componentes", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/estoque/componentes?includes=atributos`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.componentes", cache, true)

            return cache;
        }
        return null;
    }

    return LocalStorage.get("solar.componentes", true) || null;
}

export const getParametros = async (): Promise<CacheType | null> => {
    const _cached = LocalStorage.get<CacheType | null>("solar.parametros", true);
    const loading = _cached?.loading || false;

    if (!loading) {
        LocalStorage.set("solar.parametros", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/parametros_loja?id_in=PROJETOS_AVULSO_DIAS_CANCELAMENTO,PROJETOS_AVULSO_VALOR_MINIMO`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.parametros", cache, true)

            return cache;
        } else {
            LocalStorage.set("solar.parametros", { ..._cached, loading: false }, true)
        }
        return null;
    }

    return LocalStorage.get("solar.parametros", true) || null;
}

export const getCategorias = async (): Promise<CacheType | null> => {
    const _cached = LocalStorage.get<CacheType | null>("solar.categorias", true);
    const loading = _cached?.loading || false;

    if (!loading) {
        LocalStorage.set("solar.categorias", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/estoque/categorias_componentes`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache: CacheType = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.categorias", cache, true)

            return cache;
        } else {
            LocalStorage.set("solar.categorias", { ..._cached, loading: false }, true)
        }
        return null;
    }

    return LocalStorage.get("solar.categorias", true) || null;
}

export const getDistribuidores = async (): Promise<any[]> => {
    const now = moment().format("x")
    const { expires, loading } = distribuidores$.getValue();
    if (expires < Number(now) && !loading) {
        distribuidores$.next({ ...distribuidores$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `/cadastros/contas?tipo_conta_id_in=${LIST_TIPO_CONTA_DISTRIBUIDORES}&orderby=fantasia`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()
            distribuidores$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        }
        return distribuidores$.getValue().data;
    }

    return distribuidores$.getValue().data;

}


export const getPaises = async (): Promise<any[]> => {
    const now = moment().format("x")
    const { expires, loading } = paises$.getValue();
    if (expires < Number(now) && !loading) {
        paises$.next({ ...paises$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `paises?orderby=nome`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()
            paises$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        }
        return paises$.getValue().data;
    }

    return paises$.getValue().data;

}

export const getUpload = async (): Promise<CacheType | null> => {
    const now = moment().format("x")

    const _cached = LocalStorage.get<CacheType | null>("solar.uploadInfo", true);

    const expires = _cached?.expires || 0;
    const loading = _cached?.loading || false;

    if (expires < Number(now) && !loading) {
        LocalStorage.set("solar.uploadInfo", { ..._cached, loading: true }, true)
        const response: IDataResponse = await apiNoLoading.get({
            url: `/uploads`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()

            const cache = {
                data: data,
                expires: newExpireDate,
                loading: false
            }

            LocalStorage.set("solar.uploadInfo", cache, true);
            return cache;
        }
        return null;
    }

    return _cached?.data || null;

}

export const getBancos = async (): Promise<any[]> => {
    const now = moment().format("x")
    const { expires, loading } = bancos$.getValue();
    if (expires < Number(now) && !loading) {
        bancos$.next({ ...bancos$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `financeiro/bancos/listar?orderby=id`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime();
            bancos$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        }
        return bancos$.getValue().data;
    }

    return bancos$.getValue().data;

}

export const getAvulsos = async (estadoId: string): Promise<any[]> => {
    const { loading } = avulsos$.getValue();
    if (!loading) {
        avulsos$.next({ ...avulsos$.getValue(), loading: true })
        const response: IDataResponse = await apiNoLoading.get({
            url: `/estoque/componentes/avulso?estado_id=${estadoId}&reduce=1`,
        });

        if (response.statusCode === STATUS_CODE.OK) {
            const data = response.data?.rows || response.data;
            const newExpireDate = getChacheTime()
            avulsos$.next({
                data: data,
                expires: newExpireDate,
                loading: false
            })
            return data;
        } else {
            avulsos$.next({ data: [], loading: false, expires: 0 })
        }
        return avulsos$.getValue().data;
    }

    return avulsos$.getValue().data;
}